import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Loader2 } from "lucide-react";
import { CONTACT, MATERIALS } from "./data";
import { EASE } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ["You are", "Material", "Details", "Contact"];
const EMPTY = { role: "", material: "", grade: "", quantity: "", port: "", name: "", company: "", email: "", phone: "", message: "" };

const Label = ({ children }) => <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">{children}</span>;

const Field = ({ label, id, textarea, ...p }) => {
  const cls = "mt-2 w-full border-b border-white/25 bg-transparent py-3 text-white placeholder:text-slate-400 focus:border-gold-light focus:outline-none";
  return (
    <label className="block" htmlFor={id}>
      <Label>{label}</Label>
      {textarea ? <textarea id={id} rows={3} {...p} className={`${cls} resize-none`} /> : <input id={id} {...p} className={cls} />}
    </label>
  );
};

const Choice = ({ active, onClick, children, testId, big }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-2xl border text-left transition-colors duration-300 ${big ? "p-6" : "px-4 py-2.5 text-sm"} ${active ? "border-gold-light bg-gold text-white" : "border-white/20 text-slate-200 hover:border-white/60"}`}>
    {children}
  </button>
);

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

export const QuoteWizard = () => {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = (k, v) => { setF((p) => ({ ...p, [k]: v })); setStep((s) => s + 1); };
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
  const canNext = [!!f.role, !!f.material, true, f.name.trim().length > 1 && emailOk][step];

  const submit = async (e) => {
    e.preventDefault();
    if (!canNext) return;
    if (!API) { setState({ sending: false, done: false, error: "Online form unavailable. Please email us instead." }); return; }
    setState({ sending: true, done: false, error: "" });
    try {
      const payload = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || null]));
      await axios.post(`${API}/api/leads`, payload);
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      setState({ sending: false, done: false, error: errText(err) });
    }
  };

  if (state.done) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex flex-col justify-center lg:col-span-6 lg:col-start-7" data-testid="quote-success">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-gold"><Check className="h-8 w-8" /></span>
        <h3 className="mt-8 font-display text-3xl font-extrabold sm:text-4xl">Request received.</h3>
        <p className="mt-4 max-w-md text-slate-300">Thank you, {f.name.split(" ")[0]}. Our team will review your {f.material.toLowerCase()} request and get back to you at {f.email}.</p>
        <button onClick={() => { setF(EMPTY); setStep(0); setState({ sending: false, done: false, error: "" }); }} className="mt-8 w-fit text-sm font-semibold text-gold-light link-underline" data-testid="quote-new-button">Send another request</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col lg:col-span-6 lg:col-start-7" data-testid="quote-form">
      <div className="flex items-center gap-2" data-testid="quote-progress">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className="h-[3px] overflow-hidden rounded-full bg-white/15">
              <motion.div className="h-full bg-gold-light" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.5, ease: EASE }} />
            </div>
            <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.2em] ${i === step ? "text-gold-light" : "text-slate-400"}`}>{s}</p>
          </div>
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: EASE }} className="mt-10 min-h-[300px]" data-testid={`quote-step-${step}`}>
        {step === 0 && (
          <>
            <h3 className="font-display text-2xl font-bold sm:text-3xl">First, are you buying or supplying?</h3>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Choice big active={f.role === "buyer"} onClick={() => pick("role", "buyer")} testId="quote-role-buyer">
                <p className="font-display text-xl font-bold">I'm buying</p><p className="mt-1 text-sm opacity-80">Looking to import metal scrap</p>
              </Choice>
              <Choice big active={f.role === "supplier"} onClick={() => pick("role", "supplier")} testId="quote-role-supplier">
                <p className="font-display text-xl font-bold">I'm supplying</p><p className="mt-1 text-sm opacity-80">Yard, recycler or trader with material</p>
              </Choice>
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h3 className="font-display text-2xl font-bold sm:text-3xl">Which material?</h3>
            <div className="mt-8 flex flex-wrap gap-2">
              {MATERIALS.map((m) => (
                <Choice key={m.key} active={f.material === m.name} onClick={() => pick("material", m.name)} testId={`quote-material-${m.key}`}>{m.name.replace(" Scrap", "")}</Choice>
              ))}
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h3 className="font-display text-2xl font-bold sm:text-3xl">A few details <span className="text-slate-400">(optional)</span></h3>
            <div className="mt-8 grid gap-8">
              <Field label="Grade / specification" id="grade" value={f.grade} onChange={set("grade")} placeholder="e.g. Taint/Tabor, Millberry, HMS 1&2" data-testid="quote-grade-input" />
              <div className="grid gap-8 sm:grid-cols-2">
                <Field label="Quantity" id="quantity" value={f.quantity} onChange={set("quantity")} placeholder="e.g. 5 x 20ft / 100 MT" data-testid="quote-quantity-input" />
                <Field label={f.role === "supplier" ? "Loading port" : "Destination port"} id="port" value={f.port} onChange={set("port")} placeholder="e.g. Mundra, Rotterdam" data-testid="quote-port-input" />
              </div>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <h3 className="font-display text-2xl font-bold sm:text-3xl">Where should we reply?</h3>
            <div className="mt-8 grid gap-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Your name *" id="name" value={f.name} onChange={set("name")} placeholder="Full name" data-testid="quote-name-input" />
                <Field label="Company" id="company" value={f.company} onChange={set("company")} placeholder="Company name" data-testid="quote-company-input" />
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Email *" id="email" type="email" value={f.email} onChange={set("email")} placeholder="you@company.com" data-testid="quote-email-input" />
                <Field label="Phone / WhatsApp" id="phone" value={f.phone} onChange={set("phone")} placeholder="+00 000 000 000" data-testid="quote-phone-input" />
              </div>
              <Field textarea label="Anything else?" id="message" value={f.message} onChange={set("message")} placeholder="Packing, payment terms, timelines…" data-testid="quote-message-input" />
            </div>
          </>
        )}
      </motion.div>

      {state.error && (
        <p className="mt-4 text-sm text-rose-300" data-testid="quote-error">
          {state.error} <a className="underline" href={`mailto:${CONTACT.email}`}>Email us</a>
        </p>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="quote-back-button"
          className="flex items-center gap-2 text-sm font-semibold text-slate-300 transition-opacity hover:text-white disabled:opacity-0">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < 3 ? (
          <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="quote-next-button"
            className="group flex items-center gap-3 rounded-full bg-white py-2 pl-6 pr-2 text-sm font-semibold text-navy transition-colors hover:bg-gold hover:text-white disabled:cursor-not-allowed disabled:opacity-40">
            Continue
            <span className="grid h-10 w-10 place-items-center rounded-full bg-navy text-white transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
          </button>
        ) : (
          <button type="submit" disabled={!canNext || state.sending} data-testid="quote-submit-button"
            className="group flex items-center gap-3 rounded-full bg-white py-2 pl-6 pr-2 text-sm font-semibold text-navy transition-colors hover:bg-gold hover:text-white disabled:cursor-not-allowed disabled:opacity-40">
            {state.sending ? "Sending…" : "Send request"}
            <span className="grid h-10 w-10 place-items-center rounded-full bg-navy text-white">
              {state.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />}
            </span>
          </button>
        )}
      </div>
    </form>
  );
};
